import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthPage } from "@/components/AuthPage";
import { ForgotPassword } from "@/components/ForgotPassword";
import { ResetPassword } from "@/components/ResetPassword";
import { Landing } from "@/components/Landing";
import { PaymentReturn } from "@/components/PaymentReturn";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Overview } from "@/components/Overview";
import { SourcesPage, BillingPage } from "@/components/DashboardPages";
import { InstallPage } from "@/components/InstallPage";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-foreground animate-spin" />
    </div>
  );
}

// Protects the dashboard: wait while checking, redirect to /login if logged out.
function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Auth screen: if already logged in, bounce to the dashboard.
function AuthRoute({ mode }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <AuthPage initialMode={mode} />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="light" position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<AuthRoute mode="login" />} />
          <Route path="/signin" element={<AuthRoute mode="login" />} />
          <Route path="/signup" element={<AuthRoute mode="register" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<Overview />} />
            <Route path="sources" element={<SourcesPage />} />
            <Route path="test" element={<Navigate to="/dashboard" replace />} />
            <Route path="install" element={<InstallPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="/payment/success" element={<PaymentReturn />} />
          <Route path="/payment/cancel" element={<PaymentReturn />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
